import { z } from 'zod';
import { WidgetProps, ChartType } from '@sisense/sdk-ui';
import { HttpClient } from '@sisense/sdk-rest-client';
import type { OpenAI } from 'openai';

/**
 * Canonical tool description for recommendQueries — reusable by any host (sdk-assistant, MCP server, custom integrations).
 * Hosts may append host-specific notes but should not duplicate or contradict this text.
 */
export const recommendQueriesToolDescription = `Recommend natural language queries to ask on a data source based on explicit user prompt and context.

IMPORTANT:
- NEVER assume a data source title is valid. If no fresh getDataSources result exists in this conversation, call getDataSources first to validate the data source exists and is queryable (not draft), or ask the user to specify one.
- Use userPrompt to filter and prioritize relevant recommendations (e.g. "revenue dashboard", "sales analysis").
- Use existingWidgets to avoid duplicate recommendations when adding to existing dashboards.
- Extract chart types from user prompts (e.g. "bar chart", "line chart") and pass through to chart generation when the host supports it.

Do not call recommendQueries on a draft data model — drafts are not queryable until built.`;

/**
 * Default number of query recommendations to return
 */
export const DEFAULT_QUERY_COUNT = 4;

/**
 * Schema for recommendQueries engine parameters
 */
export const recommendQueriesSchema = z.object({
	dataSourceTitle: z.string().describe('Data source title'),
	queryCount: z
		.number()
		.optional()
		.describe('Number of recommended queries to return.'),
	userPrompt: z
		.string()
		.optional()
		.describe(
			'User prompt or context to guide recommendations. Used to filter and prioritize relevant queries.',
		),
	existingWidgets: z
		.array(z.string())
		.optional()
		.describe(
			'List of existing widget titles/descriptions to avoid duplicates when adding to existing dashboard.',
		),
});

/**
 * Context interface for recommendQueriesEngine
 */
export interface RecommendQueriesContext {
	/**
	 * Tool call ID
	 */
	toolCallId: string;
	/** Whether NLQ V3 is enabled */
	isNlqV3Enabled?: boolean;
	httpClient?: HttpClient;
	openAIClient?: OpenAI;
}

/**
 * Query and chart recommendation type
 */
export type QueryAndChartRecommendation = {
	detailedDescription: string;
	nlqPrompt: string;
} & WidgetProps;

/**
 * Result from recommendQueriesEngine
 */
export interface RecommendQueriesResult {
	/**
	 * List of query and chart recommendations
	 */
	recommendations: QueryAndChartRecommendation[];
}

/**
 * Callback for receiving intermediate recommendations (before chart generation)
 */
export type OnIntermediateResultsCallback = (
	recommendations: QueryAndChartRecommendation[],
) => void;

/**
 * Chart type keywords mapping for extraction from user prompts
 */
export const CHART_TYPE_KEYWORDS: Record<string, ChartType> = {
	bar: 'bar',
	column: 'column',
	line: 'line',
	pie: 'pie',
	scatter: 'scatter',
	area: 'area',
	funnel: 'funnel',
	polar: 'polar',
	treemap: 'treemap',
	sunburst: 'sunburst',
	boxplot: 'boxplot',
};
